import * as React from 'react';
import { BreadCrumb } from '@helpcenter/bread-crumb-dumb';
import { getCloudId, getHelpCenterAri } from '@helpcenter/meta';
import Breadcrumbs from '@atlaskit/breadcrumbs';
import { defineMessage, useIntl } from 'react-intl';
import { graphql, useLazyLoadQuery } from 'react-relay';
import type { csmBreadcrumbsInnerQuery } from './__generated__/csmBreadcrumbsInnerQuery.graphql';

const breadcrumbsLabel = defineMessage({
	defaultMessage: 'Breadcrumbs',
	id: 'help-center.csm-breadcrumbs.breadcrumbs-label',
	description: 'Aria label for the CSM breadcrumb navigation',
});

const breadcrumbsHome = defineMessage({
	defaultMessage: 'Home',
	id: 'help-center.csm-breadcrumbs.breadcrumbs-home',
	description: 'Home breadcrumb label for the CSM breadcrumb navigation',
});

export interface CsmBreadcrumbsProps {
	articleId: string;
	/** Optional title override — use when title isn't in Redux state (e.g. folder view). Falls back to getArticleTitle from store. */
	articleTitle?: string;
	homeUrl?: string;
}

export const CsmBreadcrumbsInner: React.FC<CsmBreadcrumbsProps> = ({
	articleId,
	articleTitle,
	homeUrl = '/',
}) => {
	const { formatMessage } = useIntl();

	const data = useLazyLoadQuery<csmBreadcrumbsInnerQuery>(
		graphql`
			query csmBreadcrumbsInnerQuery(
				$cloudId: String!
				$helpCenterAri: String!
				$articleId: String!
			) {
				helpObjectStore_searchArticlesForSupportSite(
					cloudId: $cloudId
					helpCenterAri: $helpCenterAri
					first: 1
					input: { articleIds: [$articleId], expand: [ANCESTORS] }
				) {
					nodes {
						... on HelpObjectStoreSupportSiteArticleSearchResult {
							id
							ancestors {
								id
								title
								type
							}
						}
					}
				}
			}
		`,
		{
			articleId,
			cloudId: getCloudId(),
			helpCenterAri: getHelpCenterAri(),
		},
	);

	const ancestors = data.helpObjectStore_searchArticlesForSupportSite?.nodes?.[0]?.ancestors ?? [];

	// Skip the first ancestor (index 0) — it is a top-level category/space root not relevant to the page trail
	const pageAncestors = ancestors.filter((_, index) => index !== 0);

	const currentArticleCrumb = (
		<BreadCrumb
			to={`/article/${articleId}`}
			name={articleTitle ?? ''}
			actionSubjectId="articleAncestorCurrentArticleBreadcrumb"
		/>
	);

	const ancestorChain = pageAncestors.reduceRight(
		(innerChild, ancestor) => (
			<BreadCrumb
				key={ancestor.id}
				to={ancestor.type === 'PAGE' ? `/article/${ancestor.id}` : `/folder/${ancestor.id}`}
				name={ancestor.title ?? ''}
				actionSubjectId={`articleAncestorBreadcrumb-${ancestor.id}`}
			>
				{innerChild}
			</BreadCrumb>
		),
		currentArticleCrumb,
	);

	return (
		<Breadcrumbs label={formatMessage(breadcrumbsLabel)}>
			<BreadCrumb
				to={homeUrl}
				name={formatMessage(breadcrumbsHome)}
				isFirstCrumb
				actionSubjectId="articleAncestorHomeBreadcrumb"
			>
				{ancestorChain}
			</BreadCrumb>
		</Breadcrumbs>
	);
};

export default CsmBreadcrumbsInner;
